import { tz } from "@date-fns/tz";
import { format, parseISO } from "date-fns";
import {
	History,
	LoaderIcon,
	MapPinIcon,
	RefreshCw,
	TriangleAlert,
} from "lucide-react";
import { Fragment, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	railsEntries,
	type Timeline,
	type TimelineEntry,
} from "@/lib/therapist-relocation";
import { cn } from "@/lib/utils";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import type { TherapistAddress } from "@/types/admin-portal/therapist";
import {
	AddressTimeline,
	day,
	ROLE_TAG,
	type UpcomingVisit,
} from "./address-timeline";

export function Tag({
	className,
	children,
}: {
	className: string;
	children: ReactNode;
}) {
	return (
		<span
			className={cn(
				"inline-flex shrink-0 items-center border px-1.5 py-0.5 text-xs font-semibold",
				className,
			)}
		>
			{children}
		</span>
	);
}

/**
 * The first three lines of an address, in reading order: its role with a Maps link, CITY — Province,
 * then street • postal code.
 */
export function AddressLines({
	tag,
	tagClassName,
	city,
	province,
	address,
	postalCode,
	latitude,
	longitude,
}: {
	tag: ReactNode;
	tagClassName: string;
	city: string;
	province?: string;
	address: string;
	postalCode?: string | null;
	latitude?: number | null;
	longitude?: number | null;
}) {
	const { t } = useTranslation("therapists", { keyPrefix: "form.address" });
	return (
		<>
			<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-1">
				<Tag className={tagClassName}>{tag}</Tag>
				{latitude && longitude ? (
					<a
						href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1 text-xs font-medium underline text-primary underline-offset-4"
					>
						<MapPinIcon className="size-3" />
						{t("step.maps")}
					</a>
				) : null}
			</div>
			<p className="text-sm font-semibold break-words">
				{city}
				{province && (
					<span className="font-normal text-muted-foreground">
						{" "}
						&mdash; {province}
					</span>
				)}
			</p>
			<p className="text-[13px] break-words text-muted-foreground">
				{[address, postalCode].filter(Boolean).join(" • ")}
			</p>
		</>
	);
}

/** An entry's role, range, and the one-line summary the garis waktu's segments show on hover. */
export function useEntryText() {
	const { t } = useTranslation("therapists", { keyPrefix: "form.address" });
	const { locale, timezone } = useDateContext();
	const fmt = (value: string) => format(day(value), "PP", { locale });

	const roleLabel = (entry: TimelineEntry) =>
		entry.status === "SCHEDULED" && entry.effectiveFrom
			? t("role.SCHEDULED", {
					date: format(day(entry.effectiveFrom), "d MMM", { locale }),
				})
			: t(`role.${entry.status}`);

	const until = (entry: TimelineEntry) =>
		entry.effectiveUntil
			? fmt(entry.effectiveUntil) +
				(entry.status === "ENDED" ? "" : t("meta.auto"))
			: t("meta.onward");

	const range = (entry: TimelineEntry) =>
		`${entry.effectiveFrom ? fmt(entry.effectiveFrom) : t("step.since_start")} – ${until(entry)}`;

	const scheduler = (entry: TimelineEntry) => {
		const name = entry.scheduledByName || entry.scheduledBy;
		if (!name) return null;
		// scheduledAt is an instant, unlike Go's calendar days, so it is read in the portal's zone
		return entry.scheduledAt
			? t("step.by_at", {
					name,
					date: format(parseISO(entry.scheduledAt), "PP", {
						locale,
						in: tz(timezone),
					}),
				})
			: t("step.by", { name });
	};

	// the garis waktu's hover: the same facts as a step, on one line
	const meta = (entry: TimelineEntry) =>
		[range(entry), entry.scheduledReason, scheduler(entry)]
			.filter(Boolean)
			.join(" · ");

	return { roleLabel, range, scheduler, meta };
}

/** Rails knows the province; Go's entry only names the city. Matched on the location, so a Perbaiki shows. */
const provinceOf = (entry: TimelineEntry, addresses: TherapistAddress[]) =>
	addresses.find(
		(row) =>
			row.therapistAddressId === entry.therapistAddressId &&
			row.locationId === entry.address.locationId,
	)?.location.state;

/**
 * The Alamat section's read of Go's timeline (#245 Bagian 3), shared by the edit page and the therapist
 * details: the garis waktu, then the Tangga, one step per Alamat Utama with today's line between the
 * past and the scheduled. `actions` adds the edit page's Go buttons under a step.
 */
export function AddressTimelineBlock({
	relocationSupported,
	timeline,
	addresses,
	upcomingVisits = [],
	onRetry,
	actions,
}: {
	relocationSupported: boolean;
	/** undefined while it loads */
	timeline?: ExternalProp<Timeline>;
	addresses: TherapistAddress[];
	upcomingVisits?: UpcomingVisit[];
	onRetry: () => void;
	actions?: (entry: TimelineEntry) => ReactNode;
}) {
	const { t } = useTranslation("therapists", { keyPrefix: "form.address" });
	const { t: tRelocation } = useTranslation("therapists", {
		keyPrefix: "relocation",
	});
	const { locale, timezone } = useDateContext();
	const { roleLabel, range, scheduler, meta } = useEntryText();
	const stepId = useId();
	const [showPastSteps, setShowPastSteps] = useState(false);

	if (relocationSupported && timeline === undefined)
		return (
			<output className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
				<LoaderIcon className="size-4 animate-spin" />
				{tRelocation("loading")}
			</output>
		);

	const failed = relocationSupported && !timeline?.data;
	const today =
		timeline?.data?.today ??
		format(new Date(), "yyyy-MM-dd", { in: tz(timezone) });
	const entries = timeline?.data?.entries ?? railsEntries(addresses, today);
	// Go's writes need Go's rows
	const stepActions = failed ? undefined : actions;

	const past = entries.filter((entry) => entry.status === "ENDED");
	const showPast = showPastSteps || past.length <= 1;
	const todayAt = entries.findIndex((entry) => entry.status === "SCHEDULED");
	const todayLine = (
		<li key="today" className="flex items-center gap-3 py-1">
			<span className="w-14 text-xs font-semibold text-right shrink-0">
				{t("today")}
			</span>
			<span className="flex-1 h-px bg-foreground" />
		</li>
	);
	const pick = (entry: TimelineEntry) => {
		if (entry.status === "ENDED") setShowPastSteps(true);
		// a folded step only exists after the render that unfolds it
		requestAnimationFrame(() =>
			document
				.getElementById(`${stepId}-${entry.therapistAddressId}`)
				?.scrollIntoView({ block: "nearest", behavior: "smooth" }),
		);
	};

	const steps = (
		<ol className="grid">
			{!showPast && (
				<li className="pb-2 pl-[4.25rem]">
					<Button
						type="button"
						variant="link"
						size="xs"
						className="px-0"
						onClick={() => setShowPastSteps(true)}
					>
						<History />
						{t("show_older", { count: past.length })}
					</Button>
				</li>
			)}
			{entries.map((entry, index) => {
				if (entry.status === "ENDED" && !showPast) return null;
				const by = scheduler(entry);
				return (
					<Fragment key={entry.therapistAddressId}>
						{index === todayAt && todayLine}
						<li
							id={`${stepId}-${entry.therapistAddressId}`}
							className={cn(
								"flex gap-3 scroll-mt-4",
								entry.status === "ENDED" && "opacity-75",
							)}
						>
							<span className="w-14 pt-3 text-xs text-right shrink-0 text-muted-foreground">
								{entry.effectiveFrom ? (
									<>
										<b className="block text-foreground">
											{format(day(entry.effectiveFrom), "d MMM", { locale })}
										</b>
										{format(day(entry.effectiveFrom), "yyyy")}
									</>
								) : (
									<b className="block text-foreground">{t("first")}</b>
								)}
							</span>
							<span className="relative flex justify-center w-3 shrink-0">
								<span className="absolute inset-y-0 w-px bg-border" />
								<span
									className={cn(
										"relative mt-4 size-2.5 border-2 bg-background rounded-full",
										entry.status === "CURRENT"
											? "border-primary bg-primary"
											: entry.status === "SCHEDULED"
												? "border-primary"
												: "border-muted-foreground",
									)}
								/>
							</span>
							<div className="grid flex-1 min-w-0 py-3 border-b gap-0.5">
								<AddressLines
									tag={roleLabel(entry)}
									tagClassName={ROLE_TAG[entry.status]}
									city={entry.address.city}
									province={provinceOf(entry, addresses)}
									address={entry.address.address}
									postalCode={entry.address.postalCode}
									latitude={entry.address.latitude}
									longitude={entry.address.longitude}
								/>
								<p className="mt-1.5 text-xs font-medium tabular-nums">
									{range(entry)}
								</p>
								{entry.scheduledReason && (
									<p className="text-xs break-words">
										<span className="mr-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
											{t("step.reason")}
										</span>
										{entry.scheduledReason}
									</p>
								)}
								{by && (
									<p className="text-[11px] text-muted-foreground">{by}</p>
								)}
								{stepActions && (
									<div className="mt-2">{stepActions(entry)}</div>
								)}
							</div>
						</li>
					</Fragment>
				);
			})}
			{todayAt === -1 && todayLine}
			{showPastSteps && past.length > 1 && (
				<li className="pt-2 pl-[4.25rem]">
					<Button
						type="button"
						variant="link"
						size="xs"
						className="px-0"
						onClick={() => setShowPastSteps(false)}
					>
						{t("hide_older")}
					</Button>
				</li>
			)}
		</ol>
	);

	return (
		<>
			{failed ? (
				<Alert variant="destructive">
					<TriangleAlert className="size-4" />
					<AlertTitle>{tRelocation("failed")}</AlertTitle>
					<AlertDescription className="flex flex-col items-start gap-3">
						<span>{timeline?.error}</span>
						<Button type="button" variant="outline" size="sm" onClick={onRetry}>
							<RefreshCw className="size-4" />
							{tRelocation("retry")}
						</Button>
					</AlertDescription>
				</Alert>
			) : entries.length > 0 ? (
				<AddressTimeline
					entries={entries}
					today={today}
					upcomingVisits={upcomingVisits}
					describe={meta}
					onPick={pick}
				/>
			) : null}
			{entries.length === 0 ? (
				<p className="py-4 text-sm text-muted-foreground">
					{t("none_primary")}
				</p>
			) : (
				steps
			)}
		</>
	);
}
